<script setup lang="ts">
import { onMounted, ref } from 'vue';

import BrandLogo from './BrandLogo.vue';
import { useAccount } from '@/state/account';
import { askSignOut } from '@/state/confirm-sign-out';
import { useSession } from '@/state/session';

/**
 * The screen a signed-in but unconfirmed account sees, and the only screen it
 * sees.
 *
 * **This is the client half of the verification gate.** The enforcement is
 * server-side — `EmailVerifiedGuard` refuses the garage, the economy, the battle
 * lobby and the arena ticket with a 403 — and this is what the refusal looks
 * like to a person: rather than a garage full of controls that all fail, the app
 * renders one screen saying what is needed and how to get it. So a determined
 * client can delete this component and still not get into a battle, which is the
 * property that matters.
 *
 * **It is deliberately not a modal over the garage.** A dialog implies the thing
 * behind it is usable, and none of it is. It also cannot be dismissed, for the
 * same reason: there is nothing to dismiss it to.
 *
 * **Sign out is the one way off it.** There is no door to the profile screen from
 * here any more — the two actions on this screen are resending the link and
 * signing out, deliberately at the same visual weight, neither reading as the
 * "real" one. An account stuck on a typo'd address still exists and its owner
 * can still reach it once signed back in with the corrected credential.
 *
 * It goes through the same confirmation the garage's does (`askSignOut`), which
 * matters more here than there: this screen has two buttons side by side at
 * equal weight, and the mis-tap costs somebody the session they are waiting on
 * an email for.
 *
 * Same backdrop pair as `JoinScreen.vue` and `ResetPasswordScreen.vue` — a
 * player who lands here mid-registration should not meet a visually different
 * screen than the one they just came from.
 */
const session = useSession();
const account = useAccount();

const BACKDROPS = [
  {
    image: '/assets/auth/backdrop.webp',
    webm: '/assets/auth/backdrop.webm',
    mp4: '/assets/auth/backdrop.mp4',
  },
  {
    image: '/assets/auth/backdrop2.webp',
    webm: '/assets/auth/backdrop2.webm',
    mp4: '/assets/auth/backdrop2.mp4',
  },
];
const BACKDROP = BACKDROPS[Math.floor(Math.random() * BACKDROPS.length)];
const backdropVideoReady = ref(false);
const backdropReducedMotion =
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

/**
 * The link, when the server hands one over — which it does off production only.
 *
 * With no SMTP configured `EmailService` silently no-ops, so on such a stack no
 * verification email is ever sent and the address is unconfirmable: with nothing
 * in the game reachable, that is a locked account rather than an inconvenience.
 * The token in it is a real one and is redeemed through the ordinary flow, so
 * only the delivery is shortcut. See `AccountService.resendVerification`.
 */
const devLink = ref<string | null>(null);
const cooling = ref(false);

/**
 * One is sent by `AuthService.register` already, so this screen does **not** ask
 * for another on mount — that would send two emails to every new account and burn
 * the per-account cooldown before the player had pressed anything. The address is
 * shown so a typo is visible, which is the more common problem.
 */
onMounted(() => {
  account.loadAccount().catch(() => {});
});

async function resend(): Promise<void> {
  cooling.value = true;
  devLink.value = await account.resendVerification();
  // The server enforces a per-account cooldown; this only stops the button being
  // pressed four times while the first request is still in flight.
  window.setTimeout(() => {
    cooling.value = false;
  }, 5000);
}
</script>

<template>
  <div class="landing screen" data-test="verify-gate" :style="{ backgroundImage: `url(${BACKDROP.image})` }">
    <video
      v-if="!backdropReducedMotion"
      class="backdrop-video"
      :class="{ ready: backdropVideoReady }"
      autoplay
      muted
      loop
      playsinline
      preload="auto"
      @playing="backdropVideoReady = true"
    >
      <source :src="BACKDROP.webm" type="video/webm" />
      <source :src="BACKDROP.mp4" type="video/mp4" />
    </video>
    <div class="scrim" aria-hidden="true"></div>

    <div class="panel">
      <BrandLogo class="panel-logo" aria-hidden="true" />
      <span class="sr-only">Steel of War</span>

      <h1>Confirm your email</h1>
      <p class="lede">
        You should receive an email with the activation link. You need to
        verify the address before accessing the game.
      </p>

      <p class="addr">
        Activation link sent to
        <strong data-test="verify-address">{{
          account.profile.value?.email ?? session.player.value?.email ?? 'your address'
        }}</strong
        >.
      </p>

      <div class="row">
        <button
          type="button"
          class="action"
          data-test="verify-resend"
          :disabled="account.busy.value || cooling"
          @click="resend"
        >
          {{ account.busy.value ? 'Sending…' : 'Send it again' }}
        </button>
        <button type="button" class="action" data-test="sign-out" @click="askSignOut()">
          Sign out
        </button>
      </div>

      <p v-if="account.error.value" class="error" data-test="verify-error">
        {{ account.error.value }}
      </p>

      <p v-if="devLink" class="dev" data-test="verify-dev-link">
        <span class="dev-tag">Dev</span>
        No mailer configured on this stack, so here is the link:
        <a :href="devLink">{{ devLink }}</a>
      </p>
    </div>
  </div>
</template>

<style src="../landing/landing.css"></style>

<style scoped>
.screen {
  position: relative;
  display: grid;
  place-items: center;
  height: 100%;
  padding: 2rem var(--gutter);
  overflow-y: auto;
  background-color: var(--ink);
  background-size: cover;
  background-position: center;
}

.backdrop-video {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  opacity: 0;
  transition: opacity 1.1s ease;
}

.backdrop-video.ready {
  opacity: 1;
}

.scrim {
  position: absolute;
  inset: 0;
  background:
    radial-gradient(
      ellipse 60% 50% at 50% 42%,
      color-mix(in srgb, var(--amber) 16%, transparent) 0%,
      transparent 60%
    ),
    radial-gradient(ellipse 90% 80% at 50% 50%, transparent 0%, color-mix(in srgb, var(--ink) 88%, transparent) 78%),
    color-mix(in srgb, var(--ink) 42%, transparent);
}

.panel {
  position: relative;
  z-index: 1;
  width: min(460px, calc(100vw - 2 * var(--gutter)));
  padding: 2.25rem;
  border: 1px solid var(--zinc);
  border-radius: 2px;
  background: color-mix(in srgb, var(--steel) 88%, transparent);
  backdrop-filter: blur(6px);
  box-shadow: 0 30px 70px -20px rgba(0, 0, 0, 0.65);
}

.panel-logo {
  display: block;
  margin: 0 auto 1rem;
  --brand-logo-height: 1.7rem;
}

h1 {
  margin: 0;
  text-align: center;
  font-family: var(--display);
  font-variation-settings: 'wdth' 108, 'wght' 700;
  font-size: 1.5rem;
  letter-spacing: 0.04em;
  color: var(--bone);
}

.lede {
  margin: 0.6rem 0 1.25rem;
  color: var(--frost);
  font-size: 0.875rem;
  line-height: 1.55;
}

.addr {
  margin: 0 0 1.25rem;
  padding: 0.75rem 0.9rem;
  border-radius: 3px;
  background: color-mix(in srgb, var(--ink) 60%, transparent);
  font-size: 0.85rem;
  line-height: 1.55;
  color: var(--frost);
}

.addr strong {
  color: var(--bone);
  word-break: break-all;
}

/* "Send it again" and "sign out" sit at the same level, in the same neutral
   style — neither reads as the accented default, because leaving this screen
   without a confirmed address is as legitimate a next step as staying on it. */
.row {
  display: flex;
  gap: 0.6rem;
}

.action {
  flex: 1;
  padding: 0.75rem;
  border: 1px solid var(--zinc);
  border-radius: 3px;
  background: none;
  color: var(--bone);
  font-family: var(--display);
  font-variation-settings: 'wdth' 106, 'wght' 700;
  font-size: 0.8rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  cursor: pointer;
  transition: border-color 0.2s ease, color 0.2s ease;
}

.action:hover:not(:disabled) {
  border-color: var(--frost);
}

.action:disabled {
  opacity: 0.5;
  cursor: default;
}

.error,
.dev {
  margin: 0.9rem 0 0;
  font-size: 0.8rem;
  line-height: 1.5;
}

.error {
  color: #d9483b;
}

.dev {
  padding: 0.6rem 0.75rem;
  border: 1px dashed var(--zinc);
  border-radius: 3px;
  color: var(--frost);
  word-break: break-all;
}

.dev-tag {
  display: inline-block;
  margin-right: 0.4rem;
  padding: 0.05rem 0.35rem;
  background: var(--zinc);
  color: var(--frost);
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.dev a {
  color: var(--amber);
}
</style>
